
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiMenu } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeaderProfileIcon from '../../icons/HeaderProfileIcon';
import NotificationIcon from '../../icons/NotificationIcon';
import NotificationsPanel from '../../components/NotificationsPanel';
import { rememberOAuthProvider } from '../../utils/oauth';


const GRADIENT_BG =
  'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423';


const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return (
    /iP(hone|od|ad)/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};


const ENV_BASE =
  (typeof import.meta !== 'undefined'
    ? import.meta.env?.VITE_FN_BASE
    : process.env?.REACT_APP_FN_BASE) || '';
const FN_BASE = String(ENV_BASE || '').replace(/\/$/, '');
const AUTH_BASE = FN_BASE || '/auth';


const API_BASE =
  (typeof import.meta !== 'undefined'
    ? import.meta.env?.VITE_API_BASE
    : process.env?.REACT_APP_API_BASE) || '/.netlify/functions/api';
const API_ROOT = String(API_BASE || '').replace(/\/$/, '');
const NOTIFICATIONS_UNREAD_URL = `${API_ROOT}/notifications/unread-count`;

const MobileHeader = ({ onToggleSidebar }) => {
  const { isAuthenticated, user, login, logout, error, clearError, register } = useAuth();
  const navigate = useNavigate();

  const IOS = useMemo(() => isIOS(), []);


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');

  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });


  const [registerForm, setRegisterForm] = useState({
    name: '',
    accountName: '',
    email: '',
    password: ''
  });
  const [localError, setLocalError] = useState('');


  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);


  const [openNotif, setOpenNotif] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);


  const openCreateAccount = () => {
    setModalType('create');
    setIsModalOpen(true);
    clearError();
    setRegisterForm({ name: '', accountName: '', email: '', password: '' });
    setLocalError('');
  };

  const openLogin = () => {
    setModalType('login');
    setIsModalOpen(true);
    clearError();
    setLoginForm({ identifier: '', password: '' });
    setLocalError('');
  };

  const openForgotPassword = () => {
    setModalType('forgot');
    setIsModalOpen(true);
    clearError();
    setLocalError('');
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType('');
    clearError();
    setLocalError('');
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(false);
  };


  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const ok = await login(
        String(loginForm.identifier || '').trim(),
        loginForm.password
      );
      if (ok) {
        closeModal();
      } else {
        setLocalError('تعذر تسجيل الدخول، تأكد من صحة البيانات.');
      }
    } catch {
      setLocalError('تعذر تسجيل الدخول، تأكد من صحة البيانات.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({
      ...prev,
      [name]: value
    }));
    if (error) clearError();
    setLocalError('');
  };

  const handleRegisterInputChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));
    if (error) clearError();
    setLocalError('');
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    const email = String(registerForm.email || '').trim();
    const password = String(registerForm.password || '');
    const username = String(registerForm.accountName || '').trim();
    const displayName = String((registerForm.name || '').trim());

    if (!username || !email || !password) {
      setLocalError('اسم الحساب، البريد وكلمة المرور حقول مطلوبة');
      return;
    }
    if (!/^[a-zA-Z0-9._-]{5,30}$/.test(username)) {
      setLocalError('اسم الحساب يجب أن يكون 5–30 حروف/أرقام ويمكن أن يحتوي . _ - فقط');
      return;
    }
    if (password.length < 8) {
      setLocalError('كلمة المرور يجب ألا تقل عن 8 أحرف');
      return;
    }

    try {
      const ok = await register({ email, password, username, displayName });
      if (!ok) {
        setLocalError('تعذر إنشاء الحساب. قد يكون البريد أو اسم الحساب مستخدمًا.');
        return;
      }

      setModalType('login');
      setLoginForm({ identifier: email, password: '' });
    } catch {
      setLocalError('تعذر إنشاء الحساب. حاول لاحقًا.');
    }
  };


  const handleSocialLogin = (provider) => {
    const p = String(provider || '').toLowerCase();
    let url = '';

    if (p === 'google') {
      url = `${AUTH_BASE}/google/start`;
    } else if (p === 'github') {
      url = `${AUTH_BASE}/github/start`;
    } else if (p === 'microsoft') {
      url = `${AUTH_BASE}/microsoft/start`;
    }

    if (url) {
      rememberOAuthProvider(p);
      window.location.href = url;
    } else {
      console.log(`Social login for ${provider} غير مفعّل بعد`);
    }
  };


  const handleForgotInputChange = (e) => {
    setForgotEmail(e.target.value);
    setForgotError('');
    setForgotMessage('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    const email = String(forgotEmail || '').trim();

    if (!email) {
      setForgotError('يرجى إدخال البريد الإلكتروني');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    setForgotMessage('');

    try {
      const res = await fetch(`${AUTH_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!res.ok) {
        setForgotError('حدث خطأ أثناء إرسال الرابط. حاول مرة أخرى.');
      } else {
        setForgotMessage(
          'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك إن كان مسجلاً لدينا.'
        );
      }
    } catch (err) {
      setForgotError('تعذر الاتصال بالخادم. حاول مرة أخرى.');
    } finally {
      setForgotLoading(false);
    }
  };


  useEffect(() => {
    const open = (e) => {
      setModalType(
        e.detail?.mode === 'create'
          ? 'create'
          : e.detail?.mode === 'forgot'
          ? 'forgot'
          : 'login'
      );
      setIsModalOpen(true);
      clearError?.();
    };
    window.addEventListener('open-auth-modal', open);
    return () => window.removeEventListener('open-auth-modal', open);
  }, [clearError]);


  useEffect(() => {
    if (isModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isModalOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };


  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    let isMounted = true;

    const fetchUnread = async () => {
      try {
        const res = await fetch(NOTIFICATIONS_UNREAD_URL, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          console.error('Failed to fetch unread notifications count (mobile)', res.status);
          return;
        }

        const data = await res.json();

        const count = data.count ?? data.unreadCount;
        if (isMounted && typeof count === 'number') {
          setUnreadCount(count);
        }
      } catch (err) {
        console.error('Error fetching unread notifications count (mobile)', err);
      }
    };


    fetchUnread();


    const id = setInterval(fetchUnread, 60_000);

    return () => {
      isMounted = false;
      clearInterval(id);
    };
  }, [isAuthenticated]);



  const renderCreateAccountContent = () => (
    <>
      <div
        className="flex flex-col items-center w-full"
        style={{
          height: 'auto',
          gap: '12px',
          marginBottom: '12px'
        }}
      >
        <div
          className="flex flex-row justify-center items-center w-full"
          style={{ height: '29px', gap: '22px' }}
        >
          <div
            style={{
              width: '100%',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            إنشاء حساب
          </div>
        </div>

        <div
          style={{
            width: '100%',
            height: 'auto',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '18px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          مرحبا بك
        </div>
      </div>

      {(localError || error) && (
        <div
          className="text-red-500 text-center rounded-lg w-full"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '10px 12px',
            marginBottom: '8px'
          }}
        >
          {localError || error}
        </div>
      )}

      <form onSubmit={handleRegisterSubmit} className="w-full" dir="rtl">
        <div
          className="flex flex-col justify-center items-center w-full"
          style={{ height: 'auto', gap: '12px' }}
        >
          {                }
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              placeholder="اسم الحساب"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
              name="accountName"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              inputMode="text"
              enterKeyHint="next"
              value={registerForm.accountName}
              onChange={handleRegisterInputChange}
            />
          </div>

          {                  }
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              placeholder="الاسم الكامل (اختياري)"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
              name="name"
              autoComplete="name"
              inputMode="text"
              enterKeyHint="next"
              value={registerForm.name}
              onChange={handleRegisterInputChange}
            />
          </div>

          {             }
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="email"
              placeholder="الإيميل"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
              name="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="next"
              value={registerForm.email}
              onChange={handleRegisterInputChange}
            />
          </div>

          {               }
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="password"
              placeholder="كلمة السر"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
              name="password"
              autoComplete="new-password"
              enterKeyHint="done"
              value={registerForm.password}
              onChange={handleRegisterInputChange}
            />
          </div>

          <div
            className="flex flex-row items-start w-full"
            style={{
              height: '48px',
              gap: '12px',
              justifyContent: 'space-between',
              marginTop: '6px'
            }}
          >
            <button
              type="button"
              onClick={closeModal}
              className="flex flex-row justify-center items-center rounded-xl"
              style={{
                padding: '0 24px',
                gap: '7.43px',
                minWidth: '100px',
                height: '48px',
                background: '#081423',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                إغلاق
              </span>
            </button>

            <button
              type="submit"
              className="flex flex-row justify-center items-center rounded-xl flex-1"
              style={{
                padding: '0 24px',
                gap: '7.43px',
                height: '48px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  height: '22px',
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                تسجيل
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center w-full"
        style={{
          width: '100%',
          height: 'auto',
          marginTop: '18px'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '0px',
            borderTop: '1px solid rgba(213,210,210,0.35)'
          }}
        ></div>

        <div
          className="flex flex-row items-center justify-center"
          style={{
            gap: '6px',
            width: '100%',
            height: 'auto',
            direction: 'rtl',
            paddingTop: '16px'
          }}
        >
          <span
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '150%',
              textAlign: 'center',
              color: '#D5D2D2'
            }}
          >
            هل لديك حساب؟
          </span>

          <button
            onClick={openLogin}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            سجل الدخول
          </button>
        </div>
      </div>
    </>
  );

  const renderLoginContent = () => (
    <>
      <div
        className="flex flex-col items-center w-full"
        style={{
          height: 'auto',
          gap: '12px',
          marginBottom: '12px'
        }}
      >
        <div
          className="flex flex-row justify-center items-center w-full"
          style={{ height: '29px', gap: '22px' }}
        >
          <div
            style={{
              width: '100%',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            تسجيل الدخول
          </div>
        </div>

        <div
          style={{
            width: '100%',
            height: 'auto',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '18px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          مرحبا بك
        </div>
      </div>

      {(error || localError) && (
        <div
          className="text-red-500 text-center rounded-lg w-full"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            padding: '10px 12px',
            marginBottom: '8px'
          }}
        >
          {error || localError}
        </div>
      )}

      <form onSubmit={handleLoginSubmit} className="w-full" dir="rtl">
        <div
          className="flex flex-col justify-center items-center w-full"
          style={{ height: 'auto', gap: '12px' }}
        >
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="text"
              name="identifier"
              value={loginForm.identifier}
              onChange={handleInputChange}
              placeholder="الإيميل أو اسم المستخدم"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none"
              style={{
                fontFamily: 'Alexandria',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl',
                textAlign: 'right'
              }}
              required
              autoComplete="username"
              inputMode="text"
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="next"
            />
          </div>

          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="password"
              name="password"
              value={loginForm.password}
              onChange={handleInputChange}
              placeholder="كلمة السر"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none"
              style={{
                fontFamily: 'Alexandria',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl',
                textAlign: 'right'
              }}
              required
              autoComplete="current-password"
              enterKeyHint="done"
            />
          </div>

          <div
            className="flex flex-row justify-center items-center w-full"
            style={{ height: '48px', marginTop: '6px' }}
          >
            <button
              type="submit"
              className="flex flex-row justify-center items-center rounded-xl w-full"
              style={{
                padding: '0 24px',
                height: '48px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  fontFamily: 'Alexandria',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                تسجيل الدخول
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center w-full"
        style={{ width: '100%', height: 'auto', marginTop: '18px' }}
      >
        <div
          style={{ width: '100%', height: '0px', borderTop: '1px solid rgba(213,210,210,0.35)' }}
        ></div>

        <div
          className="flex flex-col items-center w-full"
          style={{ width: '100%', gap: '12px', paddingTop: '16px' }}
        >
          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 12px',
              justifyContent: 'flex-start',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)',
              direction: 'rtl'
            }}
            type="button"
            onClick={() => handleSocialLogin('google')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.25 8.84268C16.25 11.9552 14.5925 15.7502 9.5975 15.7502C5.84346 15.7751 2.77889 12.7542 2.75 9.00018C2.77889 5.24614 5.84346 2.22521 9.5975 2.25015C11.1505 2.25591 12.6564 2.78428 13.8725 3.75015C13.9565 3.8188 14.0082 3.91936 14.015 4.02765C14.0155 4.13722 13.9723 4.24246 13.895 4.32015C13.4068 4.76655 12.9411 5.23714 12.5 5.73015C12.3717 5.87138 12.1567 5.89092 12.005 5.77515C11.3121 5.26236 10.4666 4.99814 9.605 5.02515C7.38896 5.02515 5.5925 6.82161 5.5925 9.03768C5.5925 11.2537 7.38896 13.0502 9.605 13.0502C11.855 13.0502 12.8075 12.0902 13.3025 10.3877H9.875C9.66792 10.3877 9.5 10.2198 9.5 10.0127V8.02518C9.5 7.81803 9.66792 7.65018 9.875 7.65018H15.875C16.0476 7.64906 16.1933 7.77851 16.2125 7.95018C16.2403 8.24681 16.2529 8.54471 16.25 8.84268Z"
                fill="white"
              />
            </svg>
            <span
              className="mx-auto"
              style={{
                fontFamily: 'Alexandria',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة Google
            </span>
            <span className="opacity-0 w-[19px]"></span>
          </button>

          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 12px',
              justifyContent: 'flex-start',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)',
              direction: 'rtl'
            }}
            type="button"
            onClick={() => handleSocialLogin('microsoft')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="2" y="2" width="6" height="6" fill="white" />
              <rect x="11" y="2" width="6" height="6" fill="white" />
              <rect x="2" y="10" width="6" height="6" fill="white" />
              <rect x="11" y="10" width="6" height="6" fill="white" />
            </svg>
            <span
              className="mx-auto"
              style={{
                fontFamily: 'Alexandria',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة Microsoft
            </span>
            <span className="opacity-0 w-[19px]"></span>
          </button>

          <button
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 12px',
              justifyContent: 'flex-start',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)',
              direction: 'rtl'
            }}
            type="button"
            onClick={() => handleSocialLogin('github')}
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 1C5.358 1 2 4.358 2 8.5C2 12.142 4.357 15.243 7.615 16.29C8.003 16.357 8.139 16.122 8.139 15.92C8.139 15.743 8.132 15.26 8.129 14.62C5.949 15.096 5.487 13.626 5.487 13.626C5.137 12.733 4.606 12.496 4.606 12.496C3.861 12.986 4.662 12.975 4.662 12.975C5.487 13.034 5.923 13.826 5.923 13.826C6.655 15.083 7.863 14.712 8.333 14.51C8.405 13.987 8.609 13.64 8.834 13.445C7.115 13.247 5.301 12.555 5.301 9.758C5.301 8.97 5.582 8.33 6.049 7.833C5.972 7.635 5.728 6.915 6.119 5.911C6.119 5.911 6.713 5.709 8.126 6.64C8.691 6.483 9.298 6.405 9.903 6.402C10.508 6.405 11.115 6.483 11.681 6.64C13.092 5.709 13.685 5.911 13.685 5.911C14.077 6.915 13.833 7.635 13.756 7.833C14.224 8.33 14.503 8.97 14.503 9.758C14.503 12.563 12.685 13.244 10.961 13.438C11.247 13.684 11.501 14.155 11.501 14.877C11.501 15.905 11.492 16.697 11.492 15.92C11.492 16.122 11.626 16.359 12.02 16.288C15.279 15.241 17.636 12.142 17.636 8.5C17.636 4.358 14.278 1 10.136 1H9.5Z"
                fill="white"
              />
            </svg>
            <span
              className="mx-auto"
              style={{
                fontFamily: 'Alexandria',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '17px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
            >
              سجل الدخول بواسطة GitHub
            </span>
            <span className="opacity-0 w-[19px]"></span>
          </button>
        </div>

        <div
          className="flex flex-row items-center justify-center"
          style={{ gap: '6px', width: '100%', height: 'auto', direction: 'rtl', paddingTop: '16px' }}
        >
          <span
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '150%',
              textAlign: 'center',
              color: '#D5D2D2'
            }}
          >
            ليس لديك حساب؟
          </span>

          <button
            onClick={openCreateAccount}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            سجل حساب جديد
          </button>
        </div>

        <div
          className="flex flex-row items-center justify-center"
          style={{
            gap: '4.65px',
            width: '100%',
            height: 'auto',
            direction: 'rtl',
            paddingTop: '10px'
          }}
        >
          <button
            onClick={openForgotPassword}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            نسيت كلمة السر؟
          </button>
        </div>
      </div>
    </>
  );

  const renderForgotPasswordContent = () => (
    <>
      <div
        className="flex flex-col items-center w-full"
        style={{ height: 'auto', gap: '12px', marginBottom: '12px' }}
      >
        <div
          className="flex flex-row justify-center items-center w-full"
          style={{ height: '29px', gap: '22px' }}
        >
          <div
            style={{
              width: '100%',
              height: '29px',
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '29px',
              textAlign: 'center',
              color: '#FFFFFF'
            }}
          >
            نسيت كلمة السر؟
          </div>
        </div>

        <div
          style={{
            width: '100%',
            height: 'auto',
            fontFamily: 'Alexandria',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '18px',
            textAlign: 'center',
            color: '#D5D2D2'
          }}
        >
          أدخل إيميلك لإرسال رابط إعادة تعيين كلمة السر
        </div>
      </div>

      {(forgotError || forgotMessage) && (
        <div
          className="text-center rounded-lg w-full"
          style={{
            fontFamily: 'Alexandria',
            fontSize: '14px',
            background: forgotError
              ? 'rgba(239, 68, 68, 0.1)'
              : 'rgba(34, 197, 94, 0.1)',
            border: forgotError
              ? '1px solid rgba(239, 68, 68, 0.3)'
              : '1px solid rgba(34, 197, 94, 0.3)',
            color: forgotError ? '#FCA5A5' : '#BBF7D0',
            padding: '10px 12px',
            marginBottom: '8px'
          }}
        >
          {forgotError || forgotMessage}
        </div>
      )}

      <form
        onSubmit={handleForgotSubmit}
        className="w-full"
        dir="rtl"
      >
        <div
          className="flex flex-col justify-center items-center w-full"
          style={{ height: 'auto', gap: '12px' }}
        >
          <div
            className="flex flex-row items-center rounded-xl w-full"
            style={{
              padding: '0 16px',
              gap: '7.43px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <input
              type="email"
              placeholder="الإيميل"
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-right"
              style={{
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#FFFFFF',
                direction: 'rtl'
              }}
              name="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="send"
              value={forgotEmail}
              onChange={handleForgotInputChange}
            />
          </div>

          <div
            className="flex flex-row justify-center items-center w-full"
            style={{ height: '48px', marginTop: '6px' }}
          >
            <button
              type="submit"
              disabled={forgotLoading}
              className="flex flex-row justify-center items-center rounded-xl w-full disabled:opacity-60"
              style={{
                padding: '0 24px',
                height: '48px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                borderRadius: '12px'
              }}
            >
              <span
                style={{
                  fontFamily: 'Alexandria',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#FFFFFF'
                }}
              >
                {forgotLoading ? 'جاري الإرسال...' : 'أرسل'}
              </span>
            </button>
          </div>
        </div>
      </form>

      <div
        className="flex flex-col items-center w-full"
        style={{ width: '100%', height: 'auto', marginTop: '16px' }}
      >
        <div
          style={{ width: '100%', height: '0px', borderTop: '1px solid rgba(213,210,210,0.35)' }}
        ></div>

        <div
          className="flex flex-row items-center justify-center"
          style={{
            gap: '6px',
            width: '100%',
            height: 'auto',
            direction: 'rtl',
            paddingTop: '12px'
          }}
        >
          <span
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '150%',
              textAlign: 'center',
              color: '#D5D2D2'
            }}
          >
            ليس لديك حساب؟
          </span>

          <button
            onClick={openCreateAccount}
            className="underline cursor-pointer"
            style={{
              fontFamily: 'Alexandria',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '150%',
              textAlign: 'center',
              textDecorationLine: 'underline',
              color: '#FFFFFF'
            }}
          >
            سجل حساب جديد
          </button>
        </div>
      </div>
    </>
  );


  return (
    <>
      {                 }
      <header
        className="flex items-center justify-between px-4 py-3 text-white fixed top-0 left-0 right-0 h-[60px] z-50"
        style={{
          background: GRADIENT_BG,
          backdropFilter: 'blur(37.5px)',
          WebkitBackdropFilter: 'blur(37.5px)',
          position: 'relative'
        }}
      >
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <>
              <HeaderProfileIcon onClick={() => navigate('/profile')} size="mobile" />
            </>
          ) : (
            <button
              onClick={openLogin}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-105 border"
              style={{
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                border: '1px solid rgba(40, 227, 218, 0.3)'
              }}
            >
              <svg
                width="18"
                height="14"
                viewBox="0 0 22 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 0C12.9391 0 11.9217 0.421427 11.1716 1.17157C10.4214 1.92172 10 2.93913 10 4C10 5.06087 10.4214 6.07828 11.1716 6.82843C11.9217 7.57857 12.9391 8 14 8C15.0609 8 16.0783 7.57857 16.8284 6.82843C17.5786 6.07828 18 5.06087 18 4C18 2.93913 17.5786 1.92172 16.8284 1.17157C16.0783 0.421427 15.0609 0 14 0ZM14 1.9C14.2758 1.9 14.5489 1.95432 14.8036 2.05985C15.0584 2.16539 15.2899 2.32007 15.4849 2.51508C15.6799 2.71008 15.8346 2.94158 15.9401 3.19636C16.0457 3.45115 16.1 3.72422 16.1 4C16.1 4.27578 16.0457 4.54885 15.9401 4.80364C15.8346 5.05842 15.6799 5.28992 15.4849 5.48492C15.2899 5.67993 15.0584 5.83461 14.8036 5.94015C14.5489 6.04568 14.2758 6.1 14 6.1C13.7242 6.1 13.4511 6.04568 13.1964 5.94015C12.9416 5.83461 12.7101 5.67993 12.5151 5.48492C12.3201 5.28992 12.1654 5.05842 12.0599 4.80364C11.9543 4.54885 11.9 4.27578 11.9 4C11.9 3.44305 12.1212 2.9089 12.5151 2.51508C12.9089 2.12125 13.443 1.9 14 1.9ZM3 3V6H0V8H3V11H5V8H8V6H5V3H3ZM14 9C11.33 9 6 10.33 6 13V16H22V13C22 10.33 16.67 9 14 9ZM14 10.9C16.97 10.9 20.1 12.36 20.1 13V14.1H7.9V13C7.9 12.36 11 10.9 14 10.9Z"
                  fill="#C5E6FD"
                />
              </svg>
            </button>
          )}

          {isAuthenticated && (
            <NotificationIcon
              size="mobile"
              unreadCount={unreadCount}
              onClick={() => setOpenNotif(true)}
            />
          )}

          <NotificationsPanel
            isOpen={openNotif}
            onClose={() => setOpenNotif(false)}
            variant="modal"
            onUnreadCountChange={setUnreadCount}
          />
        </div>


        <div className="flex items-center justify-center">
          <img src="/images/HemaLogo.png" alt="همة" className="h-[25px]" />
        </div>

        <div className="flex items-center">
          <button onClick={onToggleSidebar}>
            <FiMenu size={28} />
          </button>
        </div>
      </header>

      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{
              height: IOS ? '100svh' : '100dvh',
              position: 'fixed',
              overflow: 'hidden'
            }}
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm cursor-pointer"
              onClick={handleBackdropClick}
              style={{
                backdropFilter: IOS ? 'none' : 'blur(4px)',
                WebkitBackdropFilter: IOS ? 'none' : 'blur(4px)'
              }}
            ></div>

            <div
              className="relative flex flex-col justify-start items-center rounded-2xl z-10"
              style={{
                width: '92%',
                maxWidth: '24rem',
                height: 'auto',
                maxHeight: IOS ? '82svh' : '90dvh',
                padding: '24px',
                gap: '18px',
                background: GRADIENT_BG,
                borderRadius: '16px',
                border: '1px solid rgba(40, 227, 218, 0.25)',
                boxShadow: '0px 8px 32px 0px rgba(255, 255, 255, 0.08)',
                backdropFilter: IOS ? 'none' : 'blur(2px)',
                WebkitBackdropFilter: IOS ? 'none' : 'blur(2px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                paddingBottom: 'env(safe-area-inset-bottom)'
              }}
            >
              <button
                type="button"
                onClick={closeModal}
                aria-label="إغلاق"
                className="absolute top-3 left-3 h-8 w-8 rounded-full flex items-center justify-center border text-white"
                style={{ borderColor: 'rgba(40, 227, 218, 0.25)' }}
              >
                <span className="text-lg leading-none">×</span>
              </button>

              {modalType === 'create'
                ? renderCreateAccountContent()
                : modalType === 'login'
                ? renderLoginContent()
                : renderForgotPasswordContent()}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MobileHeader;
